<template>
  <BaseSimpleLayout class="user-edit">
    <template #title>ユーザ情報編集</template>
    <template #main>
      <el-form
        ref="formRef"
        :model="editForm"
        :rules="rules"
        size="large"
        label-width="120px"
      >
        <el-form-item class="unit-user-edit-id" label="ID" prop="id">
          <el-input
            v-model="editForm.id"
            autocomplete="off"
            :disabled="true"
          ></el-input>
        </el-form-item>
        <el-form-item class="unit-user-edit-name" label="ユーザ名" prop="name">
          <el-input
            v-model="editForm.name"
            autocomplete="off"
            :disabled="isConfirm"
          ></el-input>
        </el-form-item>
        <el-form-item
          class="unit-user-edit-email"
          label="メールアドレス"
          prop="email"
        >
          <el-input
            v-model="editForm.email"
            autocomplete="off"
            :disabled="isConfirm"
          ></el-input>
        </el-form-item>
        <el-form-item class="unit-user-edit-role" label="権限" prop="role">
          <el-select
            v-model="editForm.role"
            placeholder=""
            :disabled="isConfirm"
          >
            <el-option
              v-for="item in editRoleSelectOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
        </el-form-item>
        <div v-if="isConfirm">
          <el-button
            class="unit-user-edit-button-detail"
            type="info"
            @click="isConfirm = false"
          >
            キャンセル
          </el-button>
          <el-button
            class="unit-user-edit-button-confirm"
            type="primary"
            @click="update"
          >
            更新
          </el-button>
        </div>
        <div v-else>
          <el-button
            class="unit-user-edit-button-detail"
            type="info"
            @click="router.push(`/user/${editForm.id}`)"
          >
            戻る
          </el-button>
          <el-button
            class="unit-user-edit-button-confirm"
            type="primary"
            @click="changeConfirm(formRef)"
          >
            確認
          </el-button>
        </div>
      </el-form>
    </template>
  </BaseSimpleLayout>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useStore } from 'vuex';
  import router from '@/router';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import type { FormInstance, FormRules } from 'element-plus';
  import { UserPutDataState } from '@/types/user';
  import type { RolePermissionRole } from '@/store/modules/rolePermission';

  const store = useStore();
  const editForm = ref<UserPutDataState>({
    id: 0,
    name: '',
    email: '',
    role: 1
  });

  const roleSelectOptions = computed(() => {
    const roles = store.getters['rolePermission/roles'] as RolePermissionRole[];
    return roles
      .filter(
        (r) =>
          Array.isArray(r.permissionConfig) && r.permissionConfig.length > 0
      )
      .map((r) => ({ label: r.name, value: r.id }));
  });

  /** Filtered roles plus current assignment if it is not in that list (legacy / empty permissionConfig). */
  const editRoleSelectOptions = computed(() => {
    const base = roleSelectOptions.value;
    const currentId = editForm.value.role;
    if (!currentId || base.some((o) => o.value === currentId)) {
      return base;
    }
    const roles = store.getters['rolePermission/roles'] as RolePermissionRole[];
    const current = roles.find((r) => r.id === currentId);
    if (!current) {
      return base;
    }
    return [...base, { label: current.name, value: current.id }];
  });

  const rules = ref<FormRules>({
    name: [{ required: true, message: 'ユーザ名は必須です。' }],
    email: [
      {
        required: true,
        message: 'メールアドレスは必須です。'
      },
      { type: 'email', message: 'メールアドレス形式で入力してください。' }
    ],
    role: [
      {
        validator: (_rule, value: number, callback) => {
          if (!editRoleSelectOptions.value.some((o) => o.value === value)) {
            callback(new Error('権限は必須です。'));
            return;
          }
          callback();
        },
        trigger: 'change'
      }
    ]
  });
  const formRef = ref<FormInstance>();
  const isConfirm = ref(false);

  onMounted(async () => {
    try {
      await store.dispatch('rolePermission/fetchRoles');
    } catch {
      return;
    }
  });

  const initialize = async () => {
    if (!store.state.user.putData) {
      return router.push('/user');
    }
    editForm.value = store.state.user.putData;
  };
  const changeConfirm = async (formEl: FormInstance | undefined) => {
    if (!formEl) return;
    try {
      await formEl.validate();
      isConfirm.value = true;
    } catch {
      /* 各 el-form-item にエラー表示 */
    }
  };
  /** Self update → `user/put` refreshes GET /profile before redirect. */
  const update = async () => {
    await store.dispatch('user/put', editForm.value);
  };
  initialize();
</script>
<style lang="scss">
  .user-edit {
    form {
      width: 400px;
      margin: auto;
    }
    .el-select {
      width: 150px;
      float: left;
    }
    .el-button {
      margin: 10px auto;
      font-weight: 600;
    }
  }
</style>
